/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  initFirebase,
  signIn as firebaseSignIn,
  signUp as firebaseSignUp,
  signInWithGoogle as firebaseSignInWithGoogle,
  signOut as firebaseSignOut,
  getCurrentUser,
  getIdToken,
  onAuthStateChange,
} from '../services/auth/firebase';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Initialize Firebase
  useEffect(() => {
    let unsubscribe = null;

    async function initialize() {
      try {
        await initFirebase();
        setInitialized(true);

        // Subscribe to auth state changes
        unsubscribe = onAuthStateChange((firebaseUser) => {
          setUser(firebaseUser);
          setLoading(false);
        });
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setLoading(false);
      }
    }

    initialize();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Sign in
  const signIn = useCallback(async (email, password) => {
    try {
      const user = await firebaseSignIn(email, password);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to sign in',
      };
    }
  }, []);

  // Sign up
  const signUp = useCallback(async (email, password) => {
    try {
      const user = await firebaseSignUp(email, password);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to sign up',
      };
    }
  }, []);

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    try {
      const user = await firebaseSignInWithGoogle();
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to sign in with Google',
      };
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to sign out',
      };
    }
  }, []);

  // Get ID token
  const getToken = useCallback(async () => {
    return await getIdToken();
  }, []);

  // Check if authenticated
  const isAuthenticated = useCallback(() => {
    return user !== null;
  }, [user]);

  const value = {
    user,
    loading,
    initialized,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    getToken,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

